import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import Logo from '../../assets/images/logo/logo.png';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const courses = [
    { name: 'Foundation (VI-VIII)', path: '/courses#foundation' },
    { name: 'Pre-Foundation (IX-X)', path: '/courses#pre-foundation' },
    { name: 'JEE Programs', path: '/courses#jee' },
    { name: 'NEET Programs', path: '/courses#neet' },
    { name: 'Dropper Batches', path: '/courses#dropper' }
  ];

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Results', path: '/results' },
    { name: 'Faculty', path: '/faculty' },
    { name: 'Courses', path: '/courses' },
    { name: 'Contact', path: '/contact' },
    {
      name: 'Pages',
      children: [
        { name: 'Gallery', path: '/gallery' },
        { name: 'Scholarship', path: '/scholarship' },
        { name: 'Study Material', path: '/study-material' }
      ]
    },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-primary text-primary-foreground py-2 hidden md:block">
        <div className="container-custom">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <a href="tel:+918839354369" className="flex items-center space-x-2 hover:text-accent-gold transition-colors">
                <Phone className="h-4 w-4" />
                <span>+91 88393 54369</span>
              </a>
              <a href="mailto:mantrajeeneet@gmail.com" className="flex items-center space-x-2 hover:text-accent-gold transition-colors">
                <Mail className="h-4 w-4" />
                <span>mantrajeeneet@gmail.com</span>
              </a>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-accent-gold">08, Hemukalani Circle, Sindhu Nagar, Bhilwara, Rajasthan</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled
          ? 'bg-surface/95 backdrop-blur-md shadow-lg'
          : 'bg-surface'
          }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="sm:block w-52">
                <img src={Logo} className="logo" alt="" />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link, index) =>
                link.children ? (
                  /* PAGES DROPDOWN */
                  <div key={index} className="relative group">
                    <button
                      className={`px-4 py-2 rounded-md font-medium flex items-center gap-1 transition-colors
            ${location.pathname.startsWith('/results-gallery') ||
                          location.pathname.startsWith('/scholarship') ||
                          location.pathname.startsWith('/study-material')
                          ? 'text-primary bg-primary/10'
                          : 'text-foreground hover:text-primary hover:bg-primary/5'
                        }`}
                    >
                      {link.name}
                      <ChevronDown className="h-4 w-4" />
                    </button>

                    <div className="absolute top-full left-0 mt-2 w-56 bg-surface rounded-lg shadow-xl border border-border
          opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                      <div className="py-2">
                        {link.children.map((child, i) => (
                          <Link
                            key={i}
                            to={child.path}
                            className="block px-4 py-2.5 text-sm text-foreground
                  hover:bg-primary/5 hover:text-primary transition-colors"
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* NORMAL LINKS */
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-2 rounded-md font-medium transition-colors
          ${isActive(link.path)
                        ? 'text-primary bg-primary/10'
                        : 'text-foreground hover:text-primary hover:bg-primary/5'
                      }`}
                  >
                    {link.name}
                  </Link>
                )
              )}
            </nav>


            {/* CTA Button */}
            <div className="hidden lg:flex items-center space-x-4">
              <Link to="/enquiry">
                <Button className="bg-secondary hover:bg-secondary-dark text-secondary-foreground shadow-md hover:shadow-lg transition-all">
                  Enquire Now
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">

                <SheetTitle className="sr-only">
                  Mobile Navigation Menu
                </SheetTitle>

                <SheetDescription className="sr-only">
                  Opens the mobile navigation menu with website links.
                </SheetDescription>

                <div className="flex flex-col h-full">
                  <div className="pb-6 border-b border-border mb-6">
                    <h2 className="text-xl font-heading font-bold text-foreground">
                      Mantra Prayas
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">Institute of Excellence</p>
                  </div>

                  <nav className="flex-1 overflow-y-auto">
                    <div className="space-y-2">
                      {navLinks.map((link, index) =>
                        link.children ? (
                          <div key={index} className="px-2">
                            <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase">
                              {link.name}
                            </p>

                            {link.children.map((child, i) => (
                              <Link
                                key={i}
                                to={child.path}
                                onClick={() => setMobileOpen(false)}
                                className="block px-6 py-3 rounded-lg text-foreground
                hover:bg-primary/10 transition-colors"
                              >
                                {child.name}
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <Link
                            key={link.path}
                            to={link.path}
                            onClick={() => setMobileOpen(false)}
                            className={`block px-4 py-3 rounded-lg font-medium transition-colors
            ${isActive(link.path)
                                ? 'bg-primary text-primary-foreground'
                                : 'text-foreground hover:bg-primary/10'
                              }`}
                          >
                            {link.name}
                          </Link>
                        )
                      )}
                    </div>
                  </nav>


                  <div className="border-t border-border pt-6">
                    <Link to="/enquiry" onClick={() => setMobileOpen(false)}>
                      <Button className="w-full bg-secondary hover:bg-secondary-dark text-secondary-foreground">
                        Enquire Now
                      </Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>
  );
};
